import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  IconButton,
  Tooltip,
  Chip,
  Grid,
  Paper,
  Divider,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Badge,
  Snackbar
} from '@mui/material';
import {
  Add,
  Remove,
  Delete,
  ShoppingCart,
  LocalHospital,
  Upload,
  ShoppingBag,
  CardMembership,
  Close,
  Warning
} from '@mui/icons-material';
import { CiImageOff } from "react-icons/ci";
import { GiMedicinePills } from "react-icons/gi";
import userService from '../services/userService';
import useUserStore from '../stores/userStore';


const Cart = () => {
  // Zustand store
  const { cart, cartCount, fetchCart, plusMinus, deletePCart, deleteOCart } = useUserStore();
  const navigate = useNavigate();
  
  const [cartTotal, setCartTotal] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [prescriptionUploaded, setPrescriptionUploaded] = useState(false);
  const [showMembershipModal, setShowMembershipModal] = useState(false);
  const [selectedMembershipPlan, setSelectedMembershipPlan] = useState(null);
  const [membershipAdded, setMembershipAdded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingCart, setIsLoadingCart] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [imageErrors, setImageErrors] = useState({});
  const [duplicatePrescriptionItems, setDuplicatePrescriptionItems] = useState([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);

  const membershipPlans = [
    { id: 1, label: "3 months for ₹149", price: 149 },
    { id: 2, label: "6 months for ₹249", price: 249 },
    { id: 3, label: "12 months for ₹499", price: 499 },
  ];

  // Fetch cart data from API
  useEffect(() => {
    fetchCartData();
  }, []);

  // Recalculate cart total when cart changes
  useEffect(() => {
    if (cart.length > 0) {
      const calculatedTotal = cart.reduce((total, item) => {
        return total + getLineTotal(item);
      }, 0);
      setCartTotal(calculatedTotal);
    }
  }, [cart]);

  const fetchCartData = async () => {
    try {
      setIsLoadingCart(true);
      const response = await fetchCart();
      
      if (response && response.cart) {
        // Calculate cart total from cart items using best_price when available
        const calculatedTotal = response.cart.cart_items?.reduce((total, item) => {
          return total + getLineTotal(item);
        }, 0) || 0;
        
        setCartTotal(calculatedTotal);
      }
    } catch (error) {
      console.error('Error fetching cart data:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load cart data',
        severity: 'error'
      });
    } finally {
      setIsLoadingCart(false);
    }
  };

  const requiresPrescription = cart.some(item => 
    (item.product?.prescription_required) || (item.otc?.prescription_required)
  );

  // Check for duplicate prescription-required items
  const checkForDuplicates = () => {
    const prescriptionItems = cart.filter(item => 
      (item.product?.prescription_required) || (item.otc?.prescription_required)
    );
    
    // Group items by product ID
    const productGroups = {};
    prescriptionItems.forEach(item => {
      const productId = item.product ? item.product.id : item.otc.id;
      const productType = item.product ? 'product' : 'otc';
      const key = `${productType}-${productId}`;
      
      if (!productGroups[key]) {
        productGroups[key] = [];
      }
      productGroups[key].push(item);
    });
    
    // Find duplicates (groups with more than 1 item)
    const duplicates = [];
    Object.keys(productGroups).forEach(key => {
      if (productGroups[key].length > 1) {
        duplicates.push({
          key,
          items: productGroups[key],
          product: productGroups[key][0].product || productGroups[key][0].otc,
          totalQuantity: productGroups[key].reduce((sum, item) => sum + item.quantity, 0)
        });
      }
    });
    
    return duplicates;
  };

  // Check for duplicates when cart changes
  useEffect(() => {
    if (cart.length > 0) {
      const duplicates = checkForDuplicates();
      setDuplicatePrescriptionItems(duplicates);
      if (duplicates.length > 0 && !showDuplicateDialog) {
        setShowDuplicateDialog(true);
      } else if (duplicates.length === 0) {
        setShowDuplicateDialog(false);
      }
    } else {
      setDuplicatePrescriptionItems([]);
      setShowDuplicateDialog(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart.length]);

  const handleQuantityChange = async (itemId, change) => {
    try {
      const item = cart.find(item => item.id === itemId);
      if (!item) return;

      const productId = item.product ? item.product.id : item.otc.id;
      const productType = item.product ? 'product' : 'otc';
      const action = change > 0 ? "inc" : "dec";
      const cartIndex = cart.findIndex(cartItem => cartItem.id === itemId);

      // Use the store's plusMinus method which handles API calls
      await plusMinus({
        productId,
        action,
        source: 'cart',
        index: cartIndex,
        productType,
      });

      // Refresh cart data to get updated total
      await fetchCartData();

      setSnackbar({
        open: true,
        message: 'Cart updated successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error updating quantity:', error);
      setSnackbar({
        open: true,
        message: 'Failed to update cart',
        severity: 'error'
      });
      // Refresh cart data to revert optimistic update
      await fetchCartData();
    }
  };

  const handleRemoveItem = async (itemId) => {
    try {
      const item = cart.find(item => item.id === itemId);
      if (!item) return;

      // Make API call to remove item using store methods
      if (item.product) {
        await deletePCart(item.product.id);
      } else if (item.otc) {
        await deleteOCart(item.otc.id);
      }

      // Refresh cart data to get updated total
      await fetchCartData();

      setSnackbar({
        open: true,
        message: 'Item removed from cart',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing item:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove item',
        severity: 'error'
      });
      // Refresh cart data to revert optimistic update
      await fetchCartData();
    }
  };

  // Handle duplicate prescription items - remove all duplicates except first
  const handleRemoveDuplicates = async (duplicateGroup) => {
    try {
      setIsLoading(true);
      // Keep the first item, remove the rest
      const itemsToRemove = duplicateGroup.items.slice(1);
      
      for (const item of itemsToRemove) {
        if (item.product) {
          await deletePCart(item.product.id);
        } else if (item.otc) {
          await deleteOCart(item.otc.id);
        }
      }

      await fetchCartData();
      setShowDuplicateDialog(false);
      setSnackbar({
        open: true,
        message: `Removed ${itemsToRemove.length} duplicate item(s)`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error removing duplicates:', error);
      setSnackbar({
        open: true,
        message: 'Failed to remove duplicates',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle duplicate prescription items - combine quantities
  const handleCombineDuplicates = async (duplicateGroup) => {
    try {
      setIsLoading(true);
      const firstItem = duplicateGroup.items[0];
      const totalQuantity = duplicateGroup.totalQuantity;
      
      // Update first item quantity to total
      const productId = firstItem.product ? firstItem.product.id : firstItem.otc.id;
      const productType = firstItem.product ? 'product' : 'otc';
      const cartIndex = cart.findIndex(cartItem => cartItem.id === firstItem.id);
      
      // Calculate how much to add
      const quantityToAdd = totalQuantity - firstItem.quantity;
      
      if (quantityToAdd > 0) {
        for (let i = 0; i < quantityToAdd; i++) {
          await plusMinus({
            productId,
            action: 'inc',
            source: 'cart',
            index: cartIndex,
            productType,
          });
        }
      }
      
      // Remove duplicate items
      const itemsToRemove = duplicateGroup.items.slice(1);
      for (const item of itemsToRemove) {
        if (item.product) {
          await deletePCart(item.product.id);
        } else if (item.otc) {
          await deleteOCart(item.otc.id);
        }
      }

      await fetchCartData();
      setShowDuplicateDialog(false);
      setSnackbar({
        open: true,
        message: 'Combined duplicate items successfully',
        severity: 'success'
      });
    } catch (error) {
      console.error('Error combining duplicates:', error);
      setSnackbar({
        open: true,
        message: 'Failed to combine duplicates',
        severity: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleUploadPrescription = () => {
    if (selectedFile) {
      setIsLoading(true);
      // Simulate upload
      setTimeout(() => {
        setPrescriptionUploaded(true);
        setSelectedFile(null);
        setIsLoading(false);
        console.log('Prescription uploaded successfully');
      }, 1000);
    }
  };

  const handleAddMembership = (plan) => {
    setSelectedMembershipPlan(plan);
    setMembershipAdded(true);
    setShowMembershipModal(false);
  };

  const handleRemoveMembership = () => {
    setSelectedMembershipPlan(null);
    setMembershipAdded(false);
  };

  const calculateCartTotal = () => {
    // Calculate total from cart items using best_price when available
    let total = cart.reduce((sum, item) => {
      return sum + getLineTotal(item);
    }, 0);
    
    // Add membership price if added
    if (membershipAdded && selectedMembershipPlan) {
      total += selectedMembershipPlan.price;
    }
    
    return total;
  };

  const calculateMembershipSavings = () => {
    // Mock calculation - replace with actual logic
    const currentTotal = calculateCartTotal();
    const totalDiscount = currentTotal * 0.1; // 10% discount
    const percentageSavings = 10;
    return {
      differencePrice: totalDiscount.toFixed(2),
      percentageSavings: percentageSavings
    };
  };

  const handleCheckout = () => {
    // Navigate to checkout page instead of opening modal
    navigate('/checkout');
  };

  const handleProductClick = (item) => {
    const product = item.product || item.otc;
    const isOTC = !!item.otc;
    
    if (isOTC) {
      navigate(`/otc/${product.id}`);
    } else {
      navigate(`/product/${product.id}`);
    }
  };

  const formatPrice = (price) => {
    return `₹${price.toFixed(2)}`;
  };

  const getUnitPrice = (product) => {
    const price = parseFloat(product.price || 0);
    const bestPrice = parseFloat(product.best_price || 0);
    return bestPrice > 0 && bestPrice < price ? bestPrice : price;
  };

  const getLineMRP = (item) => {
    const product = item.product || item.otc;
    const price = parseFloat(product.price || 0);
    return price * item.quantity;
  };

  const getLineTotal = (item) => {
    const product = item.product || item.otc;
    return getUnitPrice(product) * item.quantity;
  };

  const getLineDiscount = (item) => {
    return getLineMRP(item) - getLineTotal(item);
  };

  const calculateCartMRP = () => {
    return cart.reduce((total, item) => total + getLineMRP(item), 0);
  };

  const calculateCartDiscount = () => {
    return cart.reduce((total, item) => total + getLineDiscount(item), 0);
  };

  const calculateCartSavingsPercent = () => {
    const mrp = calculateCartMRP();
    const discount = calculateCartDiscount();
    return mrp > 0 ? Math.round((discount / mrp) * 100) : 0;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 2 }}>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: { xs: 'column', md: 'row' }, 
        gap: 2 
      }}>
          {/* Cart Items */}
          <Box sx={{ 
            flex: { xs: '1', md: '0 0 70%' },
            maxWidth: { xs: '100%', md: '70%' }
          }}>
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ p: 2 }}>
                <Typography variant="h6" component="h1" sx={{ mb: 2, fontWeight: 600 }}>
                  Cart
                </Typography>

                {isLoadingCart ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                  </Box>
                ) : cart.length === 0 ? (
                  <Box sx={{ 
                    textAlign: 'center', 
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: 300
                  }}>
                    <ShoppingCart sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
                      Your cart is empty
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                      Add items to get started
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/')}
                      sx={{ textTransform: 'none' }}
                    >
                      Start Shopping
                    </Button>
                  </Box>
                ) : (
                  <>
                    {/* Desktop Table Layout */}
                    <TableContainer sx={{ display: { xs: 'none', md: 'block' }, mt: 1 }}>
                      <Table>
                        <TableHead>
                          <TableRow sx={{ backgroundColor: 'background.paper' }}>
                            <TableCell sx={{ fontWeight: 600, py: 1.5, backgroundColor: 'background.paper' }}>Product</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, py: 1.5, backgroundColor: 'background.paper' }}>Quantity</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 1.5, backgroundColor: 'background.paper' }}>Price</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600, py: 1.5, backgroundColor: 'background.paper' }}>Total</TableCell>
                            <TableCell align="center" sx={{ fontWeight: 600, py: 1.5, backgroundColor: 'background.paper' }}></TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {cart.map((item) => {
                            const product = item.product || item.otc;
                            const isOTC = !!item.otc;
                            const imageKey = `${item.id}-${isOTC ? 'otc' : 'product'}`;
                            const hasImageError = imageErrors[imageKey];
                            
                            const getProductImage = (images) => {
                              if (!images) return null;
                              let firstImage;
                              if (Array.isArray(images)) {
                                firstImage = images[0];
                              } else {
                                const imageNames = images.split(',');
                                firstImage = imageNames[0].trim();
                              }
                              if (Array.isArray(images)) {
                                return firstImage;
                              } else {
                                return isOTC ? `/assets/images_1mg_otc/${firstImage}` : `/assets/Drugs Images/${firstImage}`;
                              }
                            };
                            
                            const handleImageError = () => {
                              setImageErrors(prev => ({ ...prev, [imageKey]: true }));
                            };
                            
                            return (
                              <TableRow key={item.id}>
                                <TableCell>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Box 
                                      sx={{ 
                                        width: 60,
                                        height: 60,
                                        backgroundColor: 'grey.100',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        overflow: 'hidden',
                                        flexShrink: 0,
                                        cursor: 'pointer'
                                      }}
                                      onClick={() => handleProductClick(item)}
                                    >
                                      {hasImageError || !getProductImage(product.images) ? (
                                        isOTC ? (
                                          <CiImageOff size={24} />
                                        ) : (
                                          <GiMedicinePills size={24} />
                                        )
                                      ) : (
                                        <CardMedia
                                          component="img"
                                          image={getProductImage(product.images)}
                                          alt={product.name}
                                          sx={{ objectFit: 'contain', width: '100%', height: '100%' }}
                                          onError={handleImageError}
                                        />
                                      )}
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                        <Typography 
                                          variant="body2" 
                                          sx={{ 
                                            fontWeight: 500,
                                            cursor: 'pointer',
                                            wordBreak: 'break-word'
                                          }}
                                          onClick={() => handleProductClick(item)}
                                        >
                                          {product.name}
                                        </Typography>
                                        {product.prescription_required && (
                                          <LocalHospital color="error" sx={{ fontSize: 16 }} />
                                        )}
                                        {isOTC && (
                                          <Chip label="OTC" size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                                        )}
                                      </Box>
                                      {product.packaging && (
                                        <Typography variant="caption" color="text.secondary">
                                          {product.packaging}
                                        </Typography>
                                      )}
                                    </Box>
                                  </Box>
                                </TableCell>
                                <TableCell align="center">
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleQuantityChange(item.id, -1)}
                                      disabled={item.quantity <= 1}
                                      sx={{ width: 28, height: 28 }}
                                    >
                                      <Remove fontSize="small" />
                                    </IconButton>
                                    <Typography variant="body2" sx={{ minWidth: 30, textAlign: 'center', fontWeight: 500 }}>
                                      {item.quantity}
                                    </Typography>
                                    <IconButton
                                      size="small"
                                      onClick={() => handleQuantityChange(item.id, 1)}
                                      sx={{ width: 28, height: 28 }}
                                    >
                                      <Add fontSize="small" />
                                    </IconButton>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                    {getUnitPrice(product) < parseFloat(product.price || 0) && (
                                      <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                                        {formatPrice(parseFloat(product.price || 0))}
                                      </Typography>
                                    )}
                                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                      {formatPrice(getUnitPrice(product))}
                                    </Typography>
                                  </Box>
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                    {formatPrice(getLineTotal(item))}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveItem(item.id)}
                                    sx={{ color: 'error.main' }}
                                  >
                                    <Delete fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>

                    {/* Mobile Card Layout */}
                    <Box sx={{ 
                      display: { xs: 'flex', md: 'none' }, 
                      flexDirection: 'column', 
                      gap: 1.5,
                      mt: 1
                    }}>
                      {cart.map((item) => {
                        const product = item.product || item.otc;
                        const isOTC = !!item.otc;
                        const imageKey = `${item.id}-${isOTC ? 'otc' : 'product'}`;
                        const hasImageError = imageErrors[imageKey];
                        
                        const getProductImage = (images) => {
                          if (!images) return null;
                          let firstImage;
                          if (Array.isArray(images)) {
                            firstImage = images[0];
                          } else {
                            const imageNames = images.split(',');
                            firstImage = imageNames[0].trim();
                          }
                          if (Array.isArray(images)) {
                            return firstImage;
                          } else {
                            return isOTC ? `/assets/images_1mg_otc/${firstImage}` : `/assets/Drugs Images/${firstImage}`;
                          }
                        };
                        
                        const handleImageError = () => {
                          setImageErrors(prev => ({ ...prev, [imageKey]: true }));
                        };
                        
                        return (
                          <Box 
                            key={item.id} 
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'divider',
                              borderRadius: 1,
                              p: 1.5
                            }}
                          >
                            <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
                              <Box 
                                sx={{ 
                                  width: 50,
                                  height: 50,
                                  backgroundColor: 'grey.100',
                                  borderRadius: 1,
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  overflow: 'hidden',
                                  flexShrink: 0,
                                  cursor: 'pointer'
                                }}
                                onClick={() => handleProductClick(item)}
                              >
                                {hasImageError || !getProductImage(product.images) ? (
                                  isOTC ? (
                                    <CiImageOff size={20} />
                                  ) : (
                                    <GiMedicinePills size={20} />
                                  )
                                ) : (
                                  <CardMedia
                                    component="img"
                                    image={getProductImage(product.images)}
                                    alt={product.name}
                                    sx={{ objectFit: 'contain', width: '100%', height: '100%' }}
                                    onError={handleImageError}
                                  />
                                )}
                              </Box>
                              <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                                  <Typography 
                                    variant="body2" 
                                    sx={{ 
                                      fontWeight: 500,
                                      cursor: 'pointer',
                                      wordBreak: 'break-word',
                                      flex: 1
                                    }}
                                    onClick={() => handleProductClick(item)}
                                  >
                                    {product.name}
                                  </Typography>
                                  {product.prescription_required && (
                                    <LocalHospital color="error" sx={{ fontSize: 16 }} />
                                  )}
                                  {isOTC && (
                                    <Chip label="OTC" size="small" variant="outlined" sx={{ height: 18, fontSize: '0.6rem' }} />
                                  )}
                                </Box>
                                {product.packaging && (
                                  <Typography variant="caption" color="text.secondary">
                                    {product.packaging}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuantityChange(item.id, -1)}
                                  disabled={item.quantity <= 1}
                                  sx={{ width: 28, height: 28 }}
                                >
                                  <Remove fontSize="small" />
                                </IconButton>
                                <Typography variant="body2" sx={{ minWidth: 24, textAlign: 'center', fontWeight: 500 }}>
                                  {item.quantity}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => handleQuantityChange(item.id, 1)}
                                  sx={{ width: 28, height: 28 }}
                                >
                                  <Add fontSize="small" />
                                </IconButton>
                              </Box>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
                                {formatPrice(getLineTotal(item))}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveItem(item.id)}
                                sx={{ color: 'error.main' }}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Box>
                          </Box>
                        );
                      })}
                    </Box>
                  </>
                )}

                {/* Cart Total */}
                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" color="text.secondary">
                      MRP Total
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      {formatPrice(calculateCartMRP())}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="body1" color="success.main">
                      Discount
                    </Typography>
                    <Typography variant="body1" color="success.main">
                      - {formatPrice(calculateCartDiscount())}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body1" sx={{ fontWeight: 600 }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
                      {formatPrice(calculateCartTotal())}
                    </Typography>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Box>

          {/* Checkout Section */}
          <Box sx={{ 
            flex: { xs: '1', md: '0 0 30%' },
            maxWidth: { xs: '100%', md: '30%' }
          }}>
            <Box sx={{ position: 'sticky', top: 20 }}>
              {/* Order Summary Card */}
              <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
                <CardContent sx={{ p: 2 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
                    Order Summary
                  </Typography>

                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      MRP Total
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatPrice(calculateCartMRP())}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider'
                  }}>
                    <Typography variant="body2" color="success.main">
                      Discount
                    </Typography>
                    <Typography variant="body2" color="success.main">
                      - {formatPrice(calculateCartDiscount())}
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1,
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    mb: 1.5
                  }}>
                    <Typography variant="body2" color="text.secondary">
                      Delivery
                    </Typography>
                    <Typography variant="body2" color="success.main" sx={{ fontWeight: 500 }}>
                      FREE
                    </Typography>
                  </Box>
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    py: 1.5,
                    mb: 1.5
                  }}>
                    <Typography variant="body1" sx={{ fontWeight: 700 }}>
                      Total
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      {formatPrice(calculateCartTotal())}
                    </Typography>
                  </Box>
                  {calculateCartDiscount() > 0 && (
                    <Box sx={{ 
                      p: 1.5,
                      backgroundColor: 'success.light',
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'success.main',
                      mb: 1.5
                    }}>
                      <Typography variant="body2" color="success.dark" sx={{ fontWeight: 600, textAlign: 'center' }}>
                        You save {formatPrice(calculateCartDiscount())} ({calculateCartSavingsPercent()}%)
                      </Typography>
                    </Box>
                  )}

                  <Button
                    variant="contained"
                    fullWidth
                    // disabled={requiresPrescription && !prescriptionUploaded}
                    onClick={handleCheckout}
                    sx={{ 
                      mt: 1.5,
                      textTransform: 'none',
                      py: 1.2
                    }}
                  >
                    Checkout
                  </Button>
                </CardContent>
              </Card>

              {/* Prescription Upload Card */}
              {/* {requiresPrescription && (
                <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 2 }}>
                  <CardContent sx={{ p: 2 }}>
                    <Alert severity="warning" icon={<Warning />} sx={{ mb: 0 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
                        Prescription Required
                      </Typography>
                      <Typography variant="caption" sx={{ mb: 1.5, display: 'block' }}>
                        Some items require a prescription to complete purchase.
                      </Typography>
                      <input
                        accept="image/*,.pdf"
                        style={{ display: 'none' }}
                        id="prescription-upload"
                        type="file"
                        onChange={(e) => setSelectedFile(e.target.files[0])}
                      />
                      <label htmlFor="prescription-upload">
                        <Button
                          variant="outlined"
                          component="span"
                          size="small"
                          startIcon={<Upload />}
                          fullWidth
                          sx={{ textTransform: 'none', mb: 1 }}
                        >
                          Select File
                        </Button>
                      </label>
                      {selectedFile && (
                        <Typography variant="caption" display="block" sx={{ mb: 1, color: 'success.main' }}>
                          {selectedFile.name}
                        </Typography>
                      )}
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleUploadPrescription}
                          disabled={!selectedFile || isLoading}
                          sx={{ textTransform: 'none', flex: 1 }}
                        >
                          {isLoading ? 'Uploading...' : 'Upload'}
                        </Button>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => setPrescriptionUploaded(true)}
                          disabled={prescriptionUploaded}
                          sx={{ textTransform: 'none', flex: 1 }}
                        >
                          Later
                        </Button>
                      </Box>
                    </Alert>
                  </CardContent>
                </Card>
              )} */}
            </Box>
          </Box>
      </Box>

      {/* Membership Modal */}
        <Dialog open={showMembershipModal} onClose={() => setShowMembershipModal(false)} maxWidth="sm">
          <DialogTitle>Select Membership Plan</DialogTitle>
          <DialogContent>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Choose Plan</InputLabel>
              <Select
                value={selectedMembershipPlan?.id || ''}
                onChange={(e) => {
                  const plan = membershipPlans.find(p => p.id === e.target.value);
                  setSelectedMembershipPlan(plan);
                }}
                label="Choose Plan"
              >
                {membershipPlans.map((plan) => (
                  <MenuItem key={plan.id} value={plan.id}>
                    {plan.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowMembershipModal(false)}>Cancel</Button>
            <Button 
              onClick={() => handleAddMembership(selectedMembershipPlan)}
              disabled={!selectedMembershipPlan}
              variant="contained"
            >
              Add to Cart
            </Button>
          </DialogActions>
        </Dialog>

        {/* Duplicate Prescription Items Dialog */}
        <Dialog 
          open={showDuplicateDialog} 
          onClose={() => setShowDuplicateDialog(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Warning color="warning" />
              <Typography variant="h6">Duplicate Prescription Items Found</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">
                You have duplicate prescription-required items in your cart. Please choose how to fix this:
              </Typography>
            </Alert>
            
            {duplicatePrescriptionItems.map((duplicate, index) => {
              const product = duplicate.product;
              return (
                <Card key={index} variant="outlined" sx={{ mb: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                      {product.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Found {duplicate.items.length} duplicate entries with total quantity: {duplicate.totalQuantity}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleRemoveDuplicates(duplicate)}
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                      >
                        Remove Duplicates
                      </Button>
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleCombineDuplicates(duplicate)}
                        disabled={isLoading}
                        sx={{ textTransform: 'none' }}
                      >
                        Combine Quantities ({duplicate.totalQuantity})
                      </Button>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setShowDuplicateDialog(false)}
              sx={{ textTransform: 'none' }}
            >
              Continue Anyway
            </Button>
          </DialogActions>
        </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
    </Container>
  );
};

export default Cart;